'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Palette } from 'lucide-react';
import { usePalettes } from '@/features/palette';
import {
  useClassifiedProjectReferences,
  useProjectReferences,
} from '@/features/project-reference/hooks/use-project-references';
import {
  NestedSheet,
  NestedSheetContent,
  NestedSheetHeader,
  NestedSheetTitle,
  NestedSheetBody,
} from '@/shared/ui';
import { ColorsContent } from '../settings-content/ColorsContent';
import { SettingsSectionRow } from './SettingsSection';
import { ReferenceSummary } from './ReferenceSummary';

interface ColorsSectionProps {
  colors: string[] | null;
  onColorsChange: (colors: string[] | null) => void;
  hasChanges?: boolean;
  projectId?: string;
  colorProjectReferenceIds?: string[] | null;
  onColorProjectReferencesChange?: (ids: string[] | null) => void;
  useAutoColorRefs?: boolean;
  onUseAutoColorRefsChange?: (value: boolean) => void;
  onNavigateToReferences?: () => void;
  isRefine?: boolean;
}

export function ColorsSection({
  colors,
  onColorsChange,
  hasChanges = false,
  projectId,
  colorProjectReferenceIds,
  onColorProjectReferencesChange,
  useAutoColorRefs,
  onUseAutoColorRefsChange,
  onNavigateToReferences,
  isRefine = false,
}: ColorsSectionProps) {
  const t = useTranslations('render.settings.colors');
  const { palettes } = usePalettes();
  const { data: categoryRefs = [] } = useClassifiedProjectReferences(
    projectId ?? '',
    'color',
    { enabled: !!projectId }
  );
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const hasUserRefs = (colorProjectReferenceIds?.length ?? 0) > 0;
  const hasPaletteOrCustom = (colors?.length ?? 0) > 0;
  // Palette / custom colors keep their text label; references (manual or auto)
  // render as circles via ReferenceSummary.
  const showText = hasPaletteOrCustom && !hasUserRefs;

  // Selected references resolve by id across ALL references (any category):
  // the picker lets users select refs from other/unclassified categories, so a
  // category-scoped lookup dropped their thumbnails and the row wrongly showed
  // "no refs". Auto mode (allRefImages) stays category-scoped below.
  const { data: allRefs = [] } = useProjectReferences(projectId ?? '', {
    enabled: !!projectId,
  });
  const refImageUrl = (id: string) =>
    allRefs.find((r) => r.id === id)?.attributes.file_url ?? null;
  const selectedRefImages = (colorProjectReferenceIds ?? [])
    .map(refImageUrl)
    .filter((url): url is string => !!url);
  const allRefImages = categoryRefs
    .map((r) => r.attributes.file_url)
    .filter((url): url is string => !!url);

  // Palette / custom colors show their actual swatches next to the label
  // (palette name / "Custom"), mirroring Apple / Android. References render as
  // circles via ReferenceSummary.
  const swatchValue = (() => {
    if (!showText || !colors || colors.length === 0) return null;
    const isPalette = colors[0]?.startsWith('palette:');
    if (isPalette) {
      const paletteId = parseInt(colors[0]!.substring(8));
      const palette = palettes.find((p) => parseInt(p.id) === paletteId);
      return {
        hexes: palette?.attributes.colors ?? [],
        label: palette?.attributes.name ?? t('palette'),
      };
    }
    return { hexes: colors, label: t('custom') };
  })();

  const subtitleContent = swatchValue ? (
    <div className="flex items-center gap-2 min-w-0">
      {swatchValue.hexes.length > 0 && (
        <div className="flex items-center gap-px">
          {swatchValue.hexes.slice(0, 5).map((hex, i) => (
            <span
              key={`${hex}-${i}`}
              className="w-3.5 h-5 first:rounded-l-[3px] last:rounded-r-[3px]"
              style={{ backgroundColor: hex }}
            />
          ))}
        </div>
      )}
      <span className="text-list-item-subtitle text-muted-foreground truncate">
        {swatchValue.label}
      </span>
    </div>
  ) : (
    <ReferenceSummary
      selectedRefImages={selectedRefImages}
      allRefImages={allRefImages}
      useAutoRefs={useAutoColorRefs ?? true}
      isRefine={isRefine}
      autoLabel={t('auto')}
    />
  );

  return (
    <>
      <SettingsSectionRow
        icon={Palette}
        title={t('title')}
        subtitleContent={subtitleContent}
        hasChanges={hasChanges}
        onClick={() => setIsSheetOpen(true)}
      />

      <NestedSheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <NestedSheetContent>
          <NestedSheetHeader>
            <NestedSheetTitle>{t('title')}</NestedSheetTitle>
          </NestedSheetHeader>

          <NestedSheetBody className="px-4">
            <ColorsContent
              colors={colors}
              onColorsChange={onColorsChange}
              projectId={projectId}
              colorProjectReferenceIds={colorProjectReferenceIds}
              onColorProjectReferencesChange={onColorProjectReferencesChange}
              useAutoColorRefs={useAutoColorRefs}
              onUseAutoColorRefsChange={onUseAutoColorRefsChange}
              onNavigateToReferences={onNavigateToReferences}
              variant="compact"
            />
          </NestedSheetBody>
        </NestedSheetContent>
      </NestedSheet>
    </>
  );
}
